"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";

import { PUBLIC_LIST_CTA } from "@soonlist/validators";

import { capturePublicPageCta } from "~/lib/publicPageAnalytics";
import { APP_STORE_URL, createOneLink, PLAY_STORE_URL } from "~/lib/urlScheme";

interface AppStoreDownloadProps {
  className?: string;
}

interface StoreBadgesProps {
  className?: string;
  priority?: boolean;
}

export function StoreBadges({
  className = "",
  priority = false,
}: StoreBadgesProps) {
  const pathname = usePathname();
  const posthog = usePostHog();

  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-3 ${className}`}
    >
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          capturePublicPageCta(posthog, {
            cta: PUBLIC_LIST_CTA.FOOTER_APP_STORE,
            pathname,
          })
        }
        className="transition-transform duration-200 hover:scale-105"
        aria-label="Download Soonlist on the App Store"
      >
        <Image
          src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1739059200"
          alt="Download on the App Store"
          width={246}
          height={82}
          className="h-[62px] w-[186px] object-contain align-middle"
          priority={priority}
        />
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          capturePublicPageCta(posthog, {
            cta: PUBLIC_LIST_CTA.FOOTER_GOOGLE_PLAY,
            pathname,
          })
        }
        className="transition-transform duration-200 hover:scale-105"
        aria-label="Get Soonlist on Google Play"
      >
        <Image
          src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
          alt="Get it on Google Play"
          width={258}
          height={100}
          className="h-[72px] w-[186px] object-contain align-middle"
          priority={priority}
        />
      </a>
    </div>
  );
}

export function AppStoreDownload({ className = "" }: AppStoreDownloadProps) {
  const appStoreUrl = createOneLink({ campaign: "web_download_qr" });
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=192x192&data=${encodeURIComponent(
    appStoreUrl,
  )}`;

  return (
    <div className={`block ${className}`}>
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="hidden flex-col items-center justify-center md:flex">
          <div className="rounded-lg border border-neutral-3 bg-white p-3 shadow-sm">
            <Image
              src={qrCodeUrl}
              alt="Scan to download Soonlist for iPhone or Android"
              width={240}
              height={240}
              className="h-60 w-60 object-contain"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Scan with your phone or choose a store
          </p>
        </div>

        <StoreBadges />

        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-lg font-medium text-gray-700">
              Rated 4.9 / 5 on the App Store
            </span>
            <div className="flex items-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg
                  key={i}
                  className="h-5 w-5 text-amber-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Try for free. No one turned away for lack of funds.
          </p>
        </div>
      </div>
    </div>
  );
}
